import { Badge, Button } from '@auctisync/ui';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HouseSelector } from '../../components/HouseSelector';
import { PlanPanel } from '../../components/PlanPanel';
import { supabase } from '../../lib/supabase';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { useSession } from '../../hooks/useSession';

type StripeAccount = Tables<'stripe_accounts'>;

/**
 * Tenant admin — Billing & Payouts. Shows the tenant's Stripe Connect
 * account status and drives the onboarding flow via the
 * create-connect-onboarding-link Edge Function.
 *
 * The tenant comes from useOwnedTenants (review finding #9): owners of one
 * house resolve automatically; multi-house owners pick via HouseSelector
 * instead of the old "first tenant_owner role row wins" lookup.
 *
 * Module #9 ships this as the starting surface; next sessions will add
 * the payouts history + manual-adjustment tools on the same page.
 */
export function AdminBilling() {
  const { userId } = useSession();
  const location = useLocation();
  const owned = useOwnedTenants();
  const [account, setAccount] = useState<StripeAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantId = owned.selectedId;
  const tenantName = owned.selected ? (owned.selected.display_name ?? owned.selected.name) : '';

  const returnedFromOnboarding = new URLSearchParams(location.search).get('connect') === 'return';

  const loadState = useCallback(async () => {
    if (userId === null) return;
    setLoading(true);
    setError(null);
    if (owned.error) {
      setError(owned.error);
      setLoading(false);
      return;
    }
    if (!tenantId) {
      setError(
        'Billing setup is available to tenant owners. Your account is not an owner of any tenant.',
      );
      setLoading(false);
      return;
    }

    const { data: acctRow } = await supabase
      .from('stripe_accounts')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    setAccount(acctRow);
    setLoading(false);
  }, [userId, tenantId, owned.error]);

  useEffect(() => {
    // Wait for the owned-tenants lookup so the page binds to the SELECTED
    // house on first render (and re-binds when a multi-house owner switches).
    if (owned.loading) return;
    void loadState();
  }, [loadState, owned.loading]);

  // If the user just returned from Stripe's onboarding flow, poll the
  // account row for up to ~15s so the webhook has time to propagate the
  // updated status before we re-render.
  useEffect(() => {
    if (!returnedFromOnboarding || !tenantId) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const { data } = await supabase
        .from('stripe_accounts')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (data && data.onboarding_status === 'onboarded') {
        setAccount(data);
        return;
      }
      if (data) setAccount(data);
      if (attempts < 8) {
        setTimeout(() => {
          void tick();
        }, 2000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [returnedFromOnboarding, tenantId]);

  const startOnboarding = useCallback(async () => {
    if (userId === null || !tenantId) return;
    setLaunching(true);
    setError(null);
    try {
      const invokeResult = (await supabase.functions.invoke('create-connect-onboarding-link', {
        body: {
          // Pin the target house so a multi-house owner onboards the one
          // selected in the HouseSelector, not the server's first-row pick.
          tenant_id: tenantId,
          returnPath: '/admin/billing?connect=return',
          refreshPath: '/admin/billing?connect=refresh',
        },
      })) as {
        data: { url?: string; error?: string } | null;
        error: { message?: string } | null;
      };
      const data = invokeResult.data;
      const fnErr = invokeResult.error;
      if (fnErr || !data?.url) {
        const msg = data?.error ?? fnErr?.message ?? 'Could not start Stripe onboarding.';
        setError(msg);
        setLaunching(false);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLaunching(false);
    }
  }, [userId, tenantId]);

  if (loading) {
    return <div style={wrapStyle}>Loading billing settings…</div>;
  }

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Billing &amp; Payouts
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          Connect {tenantName || 'your auction house'} to Stripe so bidders can pay invoices and
          funds land directly in your bank account.
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={launching}
      />

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <section
        style={{
          padding: '1.25rem 1.5rem',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          background: 'var(--surface-elev)',
          marginBottom: '1.5rem',
        }}
      >
        <h2
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--text)',
            marginTop: 0,
            marginBottom: '0.75rem',
          }}
        >
          Stripe account
        </h2>
        <StatusBlock account={account} />
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button
            type="button"
            onClick={() => {
              void startOnboarding();
            }}
            disabled={launching || !tenantId}
          >
            {launching
              ? 'Opening Stripe…'
              : account
                ? account.onboarding_status === 'onboarded'
                  ? 'Open Stripe dashboard'
                  : 'Continue onboarding'
                : 'Connect your Stripe account'}
          </Button>
          {account && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void loadState();
              }}
            >
              Refresh status
            </Button>
          )}
        </div>
      </section>

      <PlanPanel tier={owned.selected?.tier ?? null} />

      <section
        style={{
          padding: '1rem 1.25rem',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          fontSize: '0.9rem',
          color: 'var(--text-muted)',
        }}
      >
        <strong style={{ color: 'var(--text)' }}>What happens when you click Connect:</strong>
        <ol style={{ marginTop: '0.5rem', paddingLeft: '1.1rem' }}>
          <li>AuctiSync creates a Stripe Connect account tied to your tenant</li>
          <li>You're redirected to Stripe to verify your business + bank account</li>
          <li>When you return, your account shows as &quot;connected&quot; and bidders can pay</li>
          <li>
            Funds from winning bids land in <strong>your</strong> Stripe balance and pay out to your
            bank on Stripe's normal schedule
          </li>
        </ol>
        <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          AuctiSync never holds your money — Stripe transfers directly to you. That's why AuctiSync
          doesn't need money-transmitter licenses.
        </p>
      </section>
    </div>
  );
}

function StatusBlock({ account }: { account: StripeAccount | null }) {
  if (!account) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Badge variant="outline">Not connected</Badge>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
          Click Connect to start.
        </span>
      </div>
    );
  }

  const badge =
    account.onboarding_status === 'onboarded' ? (
      <Badge variant="success">Connected</Badge>
    ) : account.onboarding_status === 'disabled' ? (
      <Badge variant="outline">Disabled</Badge>
    ) : (
      <Badge variant="outline">Onboarding in progress</Badge>
    );

  const rows: [string, string | boolean][] = [
    ['Account id', account.stripe_account_id],
    ['Charges enabled', account.charges_enabled],
    ['Payouts enabled', account.payouts_enabled],
    ['Details submitted', account.details_submitted],
    ['Country', account.country ?? '—'],
    ['Currency', account.default_currency?.toUpperCase() ?? '—'],
  ];

  return (
    <div>
      <div style={{ marginBottom: '0.75rem' }}>{badge}</div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content minmax(0, 1fr)',
          overflowWrap: 'anywhere',
          columnGap: '1rem',
          rowGap: '0.3rem',
          fontSize: '0.85rem',
          margin: 0,
        }}
      >
        {rows.map(([label, value]) => (
          <RowLine key={label} label={label} value={value} />
        ))}
      </dl>
    </div>
  );
}

function RowLine({ label, value }: { label: string; value: string | boolean }) {
  const display =
    typeof value === 'boolean' ? (value ? '✓ yes' : '— no') : value.length > 0 ? value : '—';
  return (
    <>
      <dt style={{ color: 'var(--text-muted)' }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          color: 'var(--text)',
          fontFamily:
            typeof value === 'string' && value.startsWith('acct_')
              ? 'var(--font-mono, monospace)'
              : 'inherit',
          fontSize: typeof value === 'string' && value.startsWith('acct_') ? '0.8rem' : '0.85rem',
        }}
      >
        {display}
      </dd>
    </>
  );
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '56rem',
};
